'use client';

import { DownloadSimple } from '@phosphor-icons/react';

interface GeneratedVideoOutput {
  success: boolean;
  videoUrl?: string;
  model?: string;
  mode?: 't2v' | 'i2v';
  duration?: number;
  aspectRatio?: string;
  audio?: boolean;
  error?: string;
  note?: string;
}

/**
 * Renders a single AI-generated video clip from `generateVideoClip`.
 * Plays inline with native HTML5 controls. Provides a download button.
 */
export function GeneratedVideoToolOutput({ output }: { output: GeneratedVideoOutput }) {
  if (!output.success) {
    return (
      <div className="p-3 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Video Generation Failed</p>
        <pre className="mt-1 text-xs text-muted-foreground whitespace-pre-wrap">{output.error}</pre>
      </div>
    );
  }

  if (!output.videoUrl) {
    return (
      <div className="p-3 border border-muted rounded-lg bg-muted/50">
        <p className="text-sm text-muted-foreground">Video was generated but no URL was returned.</p>
      </div>
    );
  }

  return (
    <div className="w-full border rounded-lg overflow-hidden bg-card">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/50 border-b text-xs text-muted-foreground">
        <span className="truncate">
          {output.model ?? 'AI video'}
          {output.duration ? ` · ${output.duration}s` : ''}
          {output.aspectRatio ? ` · ${output.aspectRatio}` : ''}
          {output.audio ? ' · audio' : ''}
        </span>
        <a
          href={output.videoUrl}
          download="generated-clip.mp4"
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          title="Download video"
        >
          <DownloadSimple weight="bold" className="size-3.5" />
        </a>
      </div>
      <video
        src={output.videoUrl}
        controls
        playsInline
        className="w-full h-auto block bg-black"
        style={{ maxHeight: 560 }}
      />
      {output.note && (
        <p className="px-3 py-2 text-xs text-amber-600 dark:text-amber-400 border-t bg-amber-500/10">
          {output.note}
        </p>
      )}
    </div>
  );
}
